/**
 * MaintenanceBanner Component
 * 
 * Displays a site-wide maintenance banner when VITE_MAINTENANCE_BANNER is set to 'true'.
 * The banner appears at the top of every page with a professional design that matches
 * the site's UI aesthetic.
 * 
 * Usage:
 * 1. Set VITE_MAINTENANCE_BANNER=true in your environment variables (.env.local, .env.production, etc.)
 * 2. The banner will automatically appear across the entire site
 * 3. Set VITE_MAINTENANCE_BANNER=false or remove the variable to hide the banner
 * 
 * Environment Variable:
 * - VITE_MAINTENANCE_BANNER: Set to 'true' to show banner, 'false' or undefined to hide
 * 
 * Features:
 * - High z-index (9999) to appear above all other content
 * - Responsive design that works on mobile and desktop
 * - Animated icons and elements for visual appeal
 * - Professional orange/amber gradient matching emergency/warning themes
 * - Clear, friendly messaging for users
 * 
 * Example .env.local:
 * VITE_MAINTENANCE_BANNER=true
 */
import React from 'react';
import { AlertTriangle, Wrench, Clock } from 'lucide-react';
export function MaintenanceBanner() {
  // Check if maintenance mode is enabled via environment variable
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_BANNER === 'true';
  
  // Don't render if maintenance mode is not enabled
  if (!isMaintenanceMode) {
    return null;
  }

  return (
    <div className="relative z-[9999] bg-gradient-to-r from-amber-500 to-orange-500 border-b border-amber-600/50">
      <div className="mx-auto max-w-7xl px-3 py-3">
        <div className="flex items-center justify-center gap-3 text-white">
          {/* Icon with animation */}
          <div className="flex-shrink-0">
            <div className="relative">
              <Wrench className="h-5 w-5 animate-pulse" />
              <div className="absolute -top-1 -right-1">
                <AlertTriangle className="h-3 w-3 text-amber-200" />
              </div>
            </div>
          </div>
          
          {/* Main message */}
          <div className="flex flex-col sm:flex-row items-center gap-2 text-center sm:text-left">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm sm:text-base">
                Site Maintenance in Progress
              </span>
              <Clock className="h-4 w-4 animate-spin" />
            </div>
            <div className="text-xs sm:text-sm text-amber-100">
              We're experiencing some issues and will be back online shortly. Thank you for your patience!
            </div>
          </div>
        </div>
      </div>
      
      {/* Subtle animated border effect */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-300 to-transparent animate-pulse"></div>
    </div>
  );
} 